import { useEffect, useState } from "react";
import type { Editor } from "tldraw";
import "./CanvasToolbar.css";

interface ToolDef {
  id: string;
  label: string;
  shortcut: string;
  icon: React.ReactNode;
}

const TOOLS: ToolDef[] = [
  { id: "select", label: "Select", shortcut: "V", icon: <SelectIcon /> },
  { id: "hand", label: "Hand", shortcut: "H", icon: <HandIcon /> },
  { id: "draw", label: "Draw", shortcut: "D", icon: <DrawIcon /> },
  { id: "eraser", label: "Eraser", shortcut: "E", icon: <EraserIcon /> },
  { id: "arrow", label: "Arrow", shortcut: "A", icon: <ArrowIcon /> },
  { id: "text", label: "Text", shortcut: "T", icon: <TextIcon /> },
  { id: "note", label: "Note", shortcut: "N", icon: <NoteIcon /> },
  { id: "geo", label: "Shape", shortcut: "R", icon: <RectIcon /> },
];

interface CanvasToolbarProps {
  editor: Editor | null;
  isStyleOpen: boolean;
  onToggleStyle: () => void;
}

export default function CanvasToolbar({ editor, isStyleOpen, onToggleStyle }: CanvasToolbarProps) {
  const [toolId, setToolId] = useState<string>("draw");

  useEffect(() => {
    if (!editor) return;
    setToolId(editor.getCurrentToolId());

    // Poll the editor's current tool on each animation frame so the
    // active highlight follows keyboard shortcuts, tool locks, etc.
    // Reads a cached signal value — cheap.
    let rafId = 0;
    let last = editor.getCurrentToolId();
    const tick = () => {
      const next = editor.getCurrentToolId();
      if (next !== last) {
        last = next;
        setToolId(next);
      }
      rafId = requestAnimationFrame(tick);
    };
    rafId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafId);
  }, [editor]);

  const disabled = !editor;

  return (
    <div className="canvas-toolbar">
      {TOOLS.map((tool) => (
        <button
          key={tool.id}
          className={`canvas-tool ${toolId === tool.id ? "active" : ""}`}
          title={`${tool.label} (${tool.shortcut})`}
          disabled={disabled}
          onClick={() => editor?.setCurrentTool(tool.id)}
        >
          {tool.icon}
        </button>
      ))}
      <div className="canvas-toolbar-sep" />
      <button
        className={`canvas-tool ${isStyleOpen ? "active" : ""}`}
        title="Styles"
        onClick={onToggleStyle}
      >
        <StyleIcon />
      </button>
    </div>
  );
}

function StyleIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="13.5" cy="6.5" r="1.5" />
      <circle cx="17.5" cy="10.5" r="1.5" />
      <circle cx="8.5" cy="7.5" r="1.5" />
      <circle cx="6.5" cy="12.5" r="1.5" />
      <path d="M12 2a10 10 0 1 0 0 20 3 3 0 0 0 0-6h-1.5a2.5 2.5 0 0 1 0-5H17a5 5 0 0 0 0-10 9.91 9.91 0 0 0-5 1z" />
    </svg>
  );
}

function SelectIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 3l7 17 2.5-7 7-2.5z" />
    </svg>
  );
}

function HandIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M18 11V6a2 2 0 0 0-4 0v5" />
      <path d="M14 10V4a2 2 0 0 0-4 0v6" />
      <path d="M10 10.5V6a2 2 0 0 0-4 0v8" />
      <path d="M18 8a2 2 0 1 1 4 0v6a8 8 0 0 1-8 8h-2c-2.8 0-4.5-.86-5.99-2.5l-3.5-3.5c-.7-.7-.7-1.82 0-2.5 1-.7 2.4-.7 3.4 0L8 16" />
    </svg>
  );
}

function DrawIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 19l7-7 3 3-7 7H12z" />
      <path d="M18 13l-1.5-7.5L2 2l3.5 14.5L13 18l5-5z" />
      <path d="M2 2l7.586 7.586" />
    </svg>
  );
}

function EraserIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M7 21l-4.3-4.3a1 1 0 0 1 0-1.4L13 5l6 6-8.3 8.3a1 1 0 0 1-.7.3H7.4a1 1 0 0 1-.7-.3z" />
      <path d="M22 21H7" />
    </svg>
  );
}

function ArrowIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M5 12h14" />
      <path d="M13 5l7 7-7 7" />
    </svg>
  );
}

function TextIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 7V5h16v2" />
      <path d="M12 5v14" />
      <path d="M9 19h6" />
    </svg>
  );
}

function NoteIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z" />
      <path d="M14 3v6h6" />
    </svg>
  );
}

function RectIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="4" y="6" width="16" height="12" rx="1" />
    </svg>
  );
}
